<!-- `overflow-x-clip` et non `overflow-x-hidden` : « hidden » sur un seul axe
     force l'autre en `auto` et fabrique un conteneur de défilement imbriqué,
     ce qui casserait le scroll de la page. `clip` se contente de couper. -->
<div class="overflow-x-clip" @pageFromRight>
<vw-header-padding-safe-area></vw-header-padding-safe-area>

<div class="px-4 pb-28">
  <div class="flex items-center gap-2 pb-1">
    <button
      type="button"
      class="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-primary-dark"
      (click)="goBack()"
      [attr.aria-label]="'common.back' | translate"
    >
      <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" aria-hidden="true">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <h2 class="text-[28px] font-semibold text-primary-dark">
      {{ 'lunar.title' | translate }}
    </h2>
  </div>

  <!-- Le bandeau : composant RÉUTILISÉ, pas une copie de son apparence. Il
       porte la semaine, le conseil de tradition, la phase de lune, et gère
       lui-même le flou et le cadenas du compte gratuit. -->
  <div class="mt-1">
    <vw-lunar-week
      [embedded]="true"
      (daySelected)="onDaySelected($event)"
    ></vw-lunar-week>
  </div>

  <!-- Les gestes du jour retenu : c'est ce que cette page ajoute au bandeau -->
  @if (selected && !selected.locked) {
    @if (selected.neutral) {
      <div class="mt-4 rounded-2xl bg-white p-4 shadow-sm">
        <div class="flex items-start gap-3">
          <span class="text-2xl">💤</span>
          <div class="min-w-0">
            <h3 class="text-lg font-semibold text-primary-dark">
              {{ 'lunar.neutral.title' | translate }}
            </h3>
            <p class="mt-1 text-sm leading-relaxed text-[#617069]">
              {{ 'lunar.neutral.text' | translate }}
            </p>
          </div>
        </div>
      </div>
    } @else if (selected.influence) {
      <div class="mt-4 rounded-2xl bg-white p-4 shadow-sm">
        <div class="flex items-start gap-3">
          <span class="text-2xl">{{ 'lunar.influences.' + selected.influence + '.emoji' | translate }}</span>
          <div class="min-w-0 flex-1">
            <h3 class="text-lg font-semibold text-primary-dark">
              {{ 'lunar.influences.' + selected.influence + '.title' | translate }}
            </h3>
            <p class="mt-0.5 text-sm text-[#617069]">
              {{ 'lunar.influences.' + selected.influence + '.concerns' | translate }}
            </p>
          </div>
        </div>

        <!-- Les gestes : la seule chose qui rende « jour racines » utile à qui
             ne connaît pas la tradition. -->
        <p class="mt-4 text-[11px] font-semibold uppercase tracking-wide text-[#9BA6A1]">
          {{ 'lunar.actions-title' | translate }}
        </p>
        <ul class="mt-2 flex flex-col gap-2">
          @for (i of [0, 1, 2]; track i) {
            <li class="flex items-start gap-2.5">
              <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C6BD6]"></span>
              <span class="text-sm leading-snug text-primary-dark">
                {{ 'lunar.influences.' + selected.influence + '.actions.' + i | translate }}
              </span>
            </li>
          }
        </ul>
      </div>
    }
  }

  <!-- Ce que ça change pour SON jardin — aujourd'hui seulement -->
  @if (showTodayDepth && matchedCount > 0) {
    <div class="mt-3 rounded-2xl border border-[#DCEFE3] bg-[#F2FAF5] px-4 py-3">
      <p class="text-sm text-[#1C5C41]">
        🌿 {{ 'lunar.matched' | translate: { count: matchedCount } }}
      </p>
    </div>
  }

  <!-- « À récolter » avant « à semer » : ce qui attend déjà dans le potager
       prime sur ce qu'on pourrait mettre en terre. Et c'est la seule des deux
       listes qui parle du jardin de la personne. -->
  @if (showTodayDepth && harvestSuggestions.length) {
    <h3 class="mt-6 text-lg font-semibold text-primary-dark">
      {{ 'lunar.harvest.title' | translate }}
    </h3>
    <p class="text-xs text-[#9BA6A1]">{{ 'lunar.harvest.subtitle' | translate }}</p>
    <div class="mt-2 flex flex-col gap-2">
      @for (h of harvestSuggestions; track h.plantUid) {
        <div class="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-sm">
          @if (h.imageUrl) {
            <img [src]="h.imageUrl" alt="" class="h-10 w-10 shrink-0 rounded-lg object-cover" />
          } @else {
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FDF3E3] text-lg">🧺</span>
          }
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-primary-dark">
              {{ h.name }}@if (h.variant) {<span class="font-normal text-[#617069]"> · {{ h.variant }}</span>}
            </span>
            <!-- Où la trouver : sans cela, « récolte ton persil » laisse
                 chercher dans tout le jardin. -->
            @if (h.locationName) {
              <span class="block truncate text-xs text-[#9BA6A1]">{{ h.locationName }}</span>
            }
          </span>
          <span class="shrink-0 rounded-full bg-[#FDF3E3] px-2 py-0.5 text-[11px] text-[#8A5A12]">
            {{ 'lunar.harvest.ready' | translate }}
          </span>
        </div>
      }
    </div>
  }

  <!-- Volet « à semer », visuellement séparé des cultures en place -->
  @if (showTodayDepth && sowingSuggestions.length) {
    <h3 class="mt-6 text-lg font-semibold text-primary-dark">
      {{ 'lunar.sowing.title' | translate }}
    </h3>
    <p class="text-xs text-[#9BA6A1]">{{ 'lunar.sowing.subtitle' | translate }}</p>
    <div class="mt-2 flex flex-col gap-2">
      @for (s of sowingSuggestions; track s.cultivationUid) {
        <div class="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-sm">
          @if (s.imageUrl) {
            <img [src]="s.imageUrl" alt="" class="h-10 w-10 shrink-0 rounded-lg object-cover" />
          } @else {
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EAF5EF] text-lg">🌱</span>
          }
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-primary-dark">{{ s.name }}</span>
          <span class="shrink-0 rounded-full bg-[#EAF5EF] px-2 py-0.5 text-[11px] text-[#317756]">
            {{ 'lunar.sowing.now' | translate }}
          </span>
        </div>
      }
    </div>
  }

  <!-- Gratuit : ce que la semaine complète apporte -->
  @if (!premiumService.isPremium()) {
    <div class="mt-6">
      <vw-premium-teaser
        [message]="'premium.teaser.lunar' | translate"
        [detail]="'premium.teaser.lunar-detail' | translate"
        source="lunar_page"
        (ctaClicked)="premiumPaywallOpen = true"
      ></vw-premium-teaser>
    </div>
  }

  <!-- Légende : les quatre familles, toujours consultable -->
  <h3 class="mt-6 text-lg font-semibold text-primary-dark">
    {{ 'lunar.legend' | translate }}
  </h3>
  <div class="mt-2 flex flex-col gap-2">
    @for (inf of influences; track inf) {
      <div class="flex items-start gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-sm">
        <span class="text-lg">{{ 'lunar.influences.' + inf + '.emoji' | translate }}</span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-primary-dark">
            {{ 'lunar.influences.' + inf + '.title' | translate }}
          </p>
          <p class="text-xs text-[#9BA6A1]">
            {{ 'lunar.influences.' + inf + '.concerns' | translate }}
          </p>
        </div>
      </div>
    }
  </div>
</div>
</div>

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="'lunar_page'"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>
